import { isAxiosError } from "axios";
import { z } from "zod";
import { ResumableUploadError } from "./ResumableUploadError";

const MAX_API_ATTEMPTS = 3;
const MAX_FINALIZE_STATUS_ATTEMPTS = 8;

export const UploadStatusSchema = z.object({
  upload_id: z.string().min(1),
  status: z.enum([
    "initiated",
    "finalizing",
    "finalized",
    "failed",
    "cleanup_pending",
    "expired",
  ]),
  document_file_id: z.string().min(1).nullable().optional(),
});

export const FinalizedUploadSchema = z.object({
  upload_id: z.string().min(1),
  document_file_id: z.string().min(1),
  status: z.literal("finalized"),
});

export type FinalizedUpload = z.infer<typeof FinalizedUploadSchema>;
export type UploadStatus = z.infer<typeof UploadStatusSchema>;
export type ApiRetryDelay = (attempt: number) => Promise<void>;

type FinalizeRecoveryClient = {
  readonly finalize: () => Promise<unknown>;
  readonly getStatus?: () => Promise<unknown>;
};

export function defaultApiRetryDelay(attempt: number): Promise<void> {
  const exponentialDelay = Math.min(2_000, 250 * 2 ** (attempt - 1));
  const jitter = Math.floor(Math.random() * 100);
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, exponentialDelay + jitter);
  });
}

function isTransientHttpStatus(status: number): boolean {
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function isTransientApiFailure(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || isTransientHttpStatus(status);
}

function assertNeverStatus(status: never): never {
  throw new ResumableUploadError(
    "UPLOAD_STATUS_UNKNOWN",
    `Estado de carga inesperado: ${String(status)}`,
  );
}

export async function retryTransientApiRequest<T>(
  request: () => Promise<T>,
  retryDelay: ApiRetryDelay,
): Promise<T> {
  let attempt = 1;
  while (true) {
    try {
      return await request();
    } catch (error: unknown) {
      if (!isTransientApiFailure(error) || attempt >= MAX_API_ATTEMPTS) {
        throw error;
      }
      await retryDelay(attempt);
      attempt += 1;
    }
  }
}

export async function finalizeWithReconciliation(
  client: FinalizeRecoveryClient,
  retryDelay: ApiRetryDelay,
): Promise<FinalizedUpload> {
  let finalizeError: unknown;
  try {
    return FinalizedUploadSchema.parse(await client.finalize());
  } catch (error: unknown) {
    if (!client.getStatus || !isTransientApiFailure(error)) throw error;
    finalizeError = error;
  }

  const getStatus = client.getStatus;
  for (let attempt = 1; attempt <= MAX_FINALIZE_STATUS_ATTEMPTS; attempt += 1) {
    let persisted;
    try {
      persisted = UploadStatusSchema.parse(await getStatus());
    } catch (error: unknown) {
      if (
        !isTransientApiFailure(error) ||
        attempt >= MAX_FINALIZE_STATUS_ATTEMPTS
      ) {
        throw finalizeError;
      }
      await retryDelay(attempt);
      continue;
    }

    const status = persisted.status;
    switch (status) {
      case "finalized":
        return FinalizedUploadSchema.parse(persisted);
      case "initiated":
        try {
          return FinalizedUploadSchema.parse(await client.finalize());
        } catch (error: unknown) {
          finalizeError = error;
          if (
            !isTransientApiFailure(error) ||
            attempt >= MAX_FINALIZE_STATUS_ATTEMPTS
          ) {
            throw error;
          }
          await retryDelay(attempt);
        }
        break;
      case "finalizing":
        if (attempt >= MAX_FINALIZE_STATUS_ATTEMPTS) throw finalizeError;
        await retryDelay(attempt);
        break;
      case "failed":
      case "cleanup_pending":
      case "expired":
        throw finalizeError;
      default:
        assertNeverStatus(status);
    }
  }

  throw finalizeError;
}

export async function waitForRecoveredFinalization(
  getStatus: () => Promise<unknown>,
  retryDelay: ApiRetryDelay,
): Promise<FinalizedUpload | null> {
  for (let attempt = 1; attempt <= MAX_FINALIZE_STATUS_ATTEMPTS; attempt += 1) {
    let persisted: UploadStatus;
    try {
      persisted = UploadStatusSchema.parse(await getStatus());
    } catch (error: unknown) {
      if (
        !isTransientApiFailure(error) ||
        attempt >= MAX_FINALIZE_STATUS_ATTEMPTS
      ) {
        throw error;
      }
      await retryDelay(attempt);
      continue;
    }

    switch (persisted.status) {
      case "finalized":
        return FinalizedUploadSchema.parse(persisted);
      case "initiated":
        return null;
      case "finalizing":
        if (attempt >= MAX_FINALIZE_STATUS_ATTEMPTS) {
          throw new ResumableUploadError(
            "UPLOAD_FINALIZE_PENDING",
            "La carga aún se está confirmando. Intenta nuevamente en unos segundos.",
          );
        }
        await retryDelay(attempt);
        break;
      case "failed":
      case "cleanup_pending":
      case "expired":
        throw new ResumableUploadError(
          "UPLOAD_NOT_RECOVERABLE",
          "La carga anterior ya no se puede reanudar.",
        );
      default:
        assertNeverStatus(persisted.status);
    }
  }

  throw new ResumableUploadError(
    "UPLOAD_FINALIZE_PENDING",
    "La carga aún se está confirmando. Intenta nuevamente en unos segundos.",
  );
}
